import React from 'react';
import { Link } from 'react-router-dom';
import { Network, ShieldCheck, Store, Users } from 'lucide-react';
import Badge from '../../ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { cn } from '../../../utils';

export type HierarchyNode = {
  id: string;
  name: string;
  email?: string | null;
  status?: string | null;
  meta?: string | null;
  to?: string;
};

type EntityHierarchyChartProps = {
  /** Short caption under the title */
  subtitle?: string;
  superAgent?: HierarchyNode | null;
  agent?: HierarchyNode | null;
  shops?: HierarchyNode[];
  /** Which entity is the profile currently open */
  focus: 'super_agent' | 'agent' | 'shop';
  focusedShopId?: string;
  loadingParent?: boolean;
};

const rateStatusClass = (status?: string | null): string => {
  if (status === 'active') return 'bg-green-100 text-green-700 border-green-200';
  if (status === 'suspended') return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-red-100 text-red-700 border-red-200';
};

function ChartNode({
  node,
  role,
  focused,
  icon,
}: {
  node: HierarchyNode;
  role: 'super_agent' | 'agent' | 'shop';
  focused?: boolean;
  icon: React.ReactNode;
}) {
  const tone =
    role === 'super_agent'
      ? 'border-primary/40 bg-primary/5'
      : role === 'agent'
        ? 'border-sky-200 bg-sky-50/70'
        : 'border-violet-200 bg-violet-50/60';

  const title = node.to && !focused ? (
    <Link
      to={node.to}
      className="font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
    >
      {node.name}
    </Link>
  ) : (
    <span className="font-semibold text-foreground">{node.name}</span>
  );

  return (
    <div
      className={cn(
        'relative rounded-lg border px-3 py-2.5 shadow-sm min-w-[11rem] max-w-[18rem] text-left',
        tone,
        focused && 'ring-2 ring-primary/50 shadow-md',
      )}
    >
      {focused ? (
        <span className="absolute -top-2 right-2 rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">
          Current
        </span>
      ) : null}
      <div className="flex items-start gap-2">
        <span className="mt-0.5 text-muted-foreground shrink-0">{icon}</span>
        <div className="min-w-0 space-y-1">
          <div className="truncate">{title}</div>
          {node.email ? <p className="text-xs text-muted-foreground truncate">{node.email}</p> : null}
          <div className="flex flex-wrap items-center gap-1.5">
            {node.status ? (
              <Badge variant="outline" className={cn('uppercase text-[10px] tracking-wide', rateStatusClass(node.status))}>
                {node.status}
              </Badge>
            ) : null}
            {node.meta ? <span className="text-[11px] text-muted-foreground tabular-nums">{node.meta}</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function VConnector() {
  return (
    <div className="flex flex-col items-center" aria-hidden>
      <div className="h-4 w-px bg-slate-300" />
      <div className="h-1.5 w-1.5 rounded-full bg-slate-400" />
      <div className="h-4 w-px bg-slate-300" />
    </div>
  );
}

/**
 * Org-style hierarchy chart: Super Agent → Agent → Shop(s).
 * Used on Agent / Shop profiles (and reusable for SA when needed).
 */
export const EntityHierarchyChart: React.FC<EntityHierarchyChartProps> = ({
  subtitle,
  superAgent,
  agent,
  shops = [],
  focus,
  focusedShopId,
  loadingParent,
}) => {
  const shopList =
    focus === 'shop' && focusedShopId
      ? shops.filter((s) => s.id === focusedShopId)
      : shops;

  return (
    <Card>
      <CardHeader className="space-y-1">
        <div className="flex items-center gap-2">
          <Network className="h-5 w-5 text-muted-foreground" />
          <div>
            <CardTitle className="text-lg">Hierarchy</CardTitle>
            <p className="text-xs text-muted-foreground font-normal mt-0.5">
              {subtitle ?? 'Super agent → agent → shops'}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <div className="flex min-w-[18rem] flex-col items-center gap-0 py-2">
          {loadingParent ? (
            <p className="text-sm text-muted-foreground py-2">Loading parent…</p>
          ) : superAgent ? (
            <ChartNode
              node={superAgent}
              role="super_agent"
              focused={focus === 'super_agent'}
              icon={<ShieldCheck className="h-4 w-4" />}
            />
          ) : (
            <div className="rounded-lg border border-dashed px-3 py-2 text-xs text-muted-foreground">
              No super agent linked
            </div>
          )}

          {(agent || shopList.length > 0) && <VConnector />}

          {agent ? (
            <ChartNode
              node={agent}
              role="agent"
              focused={focus === 'agent'}
              icon={<Users className="h-4 w-4" />}
            />
          ) : null}

          {agent && shopList.length > 0 ? <VConnector /> : null}

          {shopList.length > 0 ? (
            <div className="relative w-full max-w-4xl px-2">
              {shopList.length > 1 ? (
                <div
                  className="absolute left-[12.5%] right-[12.5%] top-0 h-px bg-slate-300"
                  aria-hidden
                />
              ) : null}
              <ul
                className={cn(
                  'grid gap-3 pt-3',
                  shopList.length === 1
                    ? 'grid-cols-1 justify-items-center'
                    : shopList.length === 2
                      ? 'grid-cols-2'
                      : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
                )}
              >
                {shopList.map((shop) => (
                  <li key={shop.id} className="relative flex flex-col items-center">
                    {shopList.length > 1 ? (
                      <div className="absolute -top-3 h-3 w-px bg-slate-300" aria-hidden />
                    ) : null}
                    <ChartNode
                      node={shop}
                      role="shop"
                      focused={focus === 'shop' && shop.id === focusedShopId}
                      icon={<Store className="h-4 w-4" />}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ) : agent && focus !== 'shop' ? (
            <p className="mt-2 text-sm text-muted-foreground">No shops under this agent.</p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default EntityHierarchyChart;
